<template>
  <div
    v-if="showBubble"
    :class="bubbleWrapperClasses"
    class="my-16"
    data-message-item="true"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <MessageBubbleAdmin
      v-if="isAdmin"
      :body="body"
      :adminMessageData="adminMessageData"
      :accountType="accountType"
      :ariaLabel="ariaLabel"
    />
    <MessageBubbleIncoming
      v-else-if="!isOutgoing"
      :body="body"
      :senderName="senderName"
      :senderAvatarUrl="senderAvatarUrl"
      :timestamp="timestamp"
      :ariaLabel="ariaLabel"
      :showActions="showActions"
    />
    <MessageBubbleOutgoing
      v-else
      :body="body"
      :timestamp="timestamp"
      :ariaLabel="ariaLabel"
      :read="read"
      :messageStatus="messageStatus"
      :showActions="showActions"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { MessageStatus } from '@/apps/messages/types';
import MessageBubbleAdmin from './MessageBubbleAdmin.vue';
import MessageBubbleIncoming from './MessageBubbleIncoming.vue';
import MessageBubbleOutgoing from './MessageBubbleOutgoing.vue';
import type { AdminMessageData } from '@/apps/messages/helpers/adminMessageFormatter';

export default {
  name: 'MessageBubble',
  components: {
    MessageBubbleAdmin,
    MessageBubbleIncoming,
    MessageBubbleOutgoing,
  },
  props: {
    body: {
      type: String,
      default: '',
    },
    adminMessageData: {
      type: Object as PropType<AdminMessageData | null>,
      default: null,
    },
    senderName: {
      type: String,
      required: true,
    },
    senderAvatarUrl: {
      type: String,
      default: '',
    },
    isOutgoing: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    timestamp: {
      type: String,
      default: '',
    },
    read: {
      type: Boolean,
      default: false,
    },
    messageStatus: {
      type: String as PropType<MessageStatus>,
      default: 'sent',
    },
    accountType: {
      type: String as PropType<'parent' | 'sitter'>,
      required: true,
    },
  },
  data() {
    return {
      showActions: false,
      isTouchDevice: false,
    };
  },
  computed: {
    showBubble(): boolean {
      if (!this.isAdmin) return true;
      const hasStructured = this.adminMessageData != null;
      const hasBody = typeof this.body === 'string' && this.body.trim().length > 0;
      return hasStructured || hasBody;
    },
    bubbleWrapperClasses(): string {
      if (this.isAdmin) return 'flex justify-center';
      return this.isOutgoing ? 'flex justify-end' : 'flex items-start gap-12';
    },
    ariaLabel(): string {
      if (this.isAdmin) {
        if (this.adminMessageData) return 'System message';
        return this.body ? `System message: ${this.body}` : 'System message';
      }
      const directionLabel = this.isOutgoing ? 'You' : this.senderName;
      const parts = [`${directionLabel} said ${this.body}`];
      if (this.timestamp) {
        parts.push(`at ${this.timestamp}`);
      }
      return parts.join(' ');
    },
  },
  mounted() {
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (this.isTouchDevice) {
      document.addEventListener('click', this.handleDocumentClick);
    }
  },
  beforeUnmount() {
    if (this.isTouchDevice) {
      document.removeEventListener('click', this.handleDocumentClick);
    }
  },
  methods: {
    handleMouseEnter(): void {
      if (!this.isTouchDevice) {
        this.showActions = true;
      }
    },
    handleMouseLeave(): void {
      if (!this.isTouchDevice) {
        this.showActions = false;
      }
    },
    handleClick(): void {
      if (this.isTouchDevice) {
        this.showActions = !this.showActions;
      }
    },
    handleDocumentClick(event): void {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showActions = false;
      }
    },
  },
};
</script>
